import React, { useState } from 'react';
import { Input, Textarea, Tooltip } from '@fluentui/react-components';
import { Delete16Regular, Info16Regular } from '@fluentui/react-icons';
import setupStyles from '../WorkspaceSetup/WorkspaceSetupWizard.module.css';
import {
    IDEAL_CUSTOMER_PROFILE_PLACEHOLDER,
    OFFERINGS_PLACEHOLDER,
    ORG_DESCRIPTION_PLACEHOLDER,
    WORKSPACE_ORG_TYPES,
} from '../../utils/workspaceOrgProfile';
import {
    COMPANY_VALUE_BEHAVIOR_PLACEHOLDER,
    COMPANY_VALUE_DESCRIPTION_PLACEHOLDER,
    COMPANY_VALUE_NAME_PLACEHOLDER,
    EMPTY_COMPANY_VALUE,
    PULSE_FIELD_META,
    PULSE_PREFERENCE_KEYS,
    normalizeCompanyValues,
} from '../../utils/workspacePulseProfile';

const WorkspaceProfileSection = ({ profile, onChange }) => {
    const [valueDraft, setValueDraft] = useState(EMPTY_COMPANY_VALUE);
    const [editingValueIndex, setEditingValueIndex] = useState(null);

    const companyValues = normalizeCompanyValues(profile?.company_values);

    const updateField = (key, value) => {
        onChange({ [key]: value });
    };

    const updateValueDraftField = (key, value) => {
        setValueDraft(prev => ({ ...prev, [key]: value }));
    };

    const handleEditSavedValue = index => {
        const value = companyValues[index];
        if (!value) return;
        setEditingValueIndex(index);
        setValueDraft({
            name: value.name || '',
            description: value.description || '',
            expected_behavior: value.expected_behavior || '',
        });
    };

    const handleSaveValueAndAddAnother = () => {
        const name = (valueDraft?.name || '').trim();
        if (!name) return;
        const entry = {
            name,
            description: (valueDraft?.description || '').trim(),
            expected_behavior: (valueDraft?.expected_behavior || '').trim(),
        };
        const nextValues = [...companyValues];
        if (editingValueIndex !== null) {
            nextValues[editingValueIndex] = entry;
        } else {
            nextValues.push(entry);
        }
        onChange({ company_values: nextValues });
        setValueDraft(EMPTY_COMPANY_VALUE);
        setEditingValueIndex(null);
    };

    const handleDeleteSavedValue = index => {
        const nextValues = companyValues.filter((_, i) => i !== index);
        onChange({ company_values: nextValues });
        if (editingValueIndex === index) {
            setValueDraft(EMPTY_COMPANY_VALUE);
            setEditingValueIndex(null);
        } else if (editingValueIndex !== null && editingValueIndex > index) {
            setEditingValueIndex(editingValueIndex - 1);
        }
    };

    const canSaveValue = (valueDraft?.name || '').trim().length > 0;

    const renderPulseField = fieldKey => {
        const meta = PULSE_FIELD_META[fieldKey];
        return (
            <div key={fieldKey} className={setupStyles.pulseGridItem}>
                <div className={setupStyles.pulseFieldLabelRow}>
                    <p className={`${setupStyles.fieldLabel} mb-0`}>{meta.label}</p>
                    <Tooltip positioning="above" withArrow content={meta.description}>
                        <Info16Regular
                            color="var(--Neutral-Gray-6)"
                            role="button"
                            tabIndex={0}
                            className={setupStyles.pulseInfoIcon}
                            aria-label={`${meta.label} info`}
                        />
                    </Tooltip>
                </div>
                <select
                    className={setupStyles.pulseSelect}
                    value={profile?.[fieldKey] || ''}
                    onChange={e => updateField(fieldKey, e.target.value)}
                    aria-label={meta.label}>
                    <option value="">Select</option>
                    {meta.options.map(option => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </select>
            </div>
        );
    };

    return (
        <>
            <div className={setupStyles.orgTextField}>
                <p className={setupStyles.fieldLabel}>Organisation Type</p>
                <div className={setupStyles.orgSelectWrap}>
                    <div className={setupStyles.orgSelect}>
                        <select value={profile?.org_type || ''} onChange={e => updateField('org_type', e.target.value)} name="org_type">
                            <option value="">Select</option>
                            {WORKSPACE_ORG_TYPES.map(type => (
                                <option key={type} value={type}>
                                    {type}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className={setupStyles.orgTextField}>
                <p className={setupStyles.fieldLabel}>Organisation Description</p>
                <Textarea
                    className={setupStyles.orgTextarea}
                    value={profile?.org_description || ''}
                    onChange={(e, data) => updateField('org_description', data.value)}
                    rows={6}
                    placeholder={ORG_DESCRIPTION_PLACEHOLDER}
                />
            </div>

            <div className={setupStyles.orgTextField}>
                <p className={setupStyles.fieldLabel}>Offerings</p>
                <Textarea
                    className={setupStyles.orgTextarea}
                    value={profile?.offerings || ''}
                    onChange={(e, data) => updateField('offerings', data.value)}
                    rows={6}
                    placeholder={OFFERINGS_PLACEHOLDER}
                />
            </div>

            <div className={setupStyles.orgTextField}>
                <p className={setupStyles.fieldLabel}>Ideal Customer Profile</p>
                <Textarea
                    className={setupStyles.orgTextarea}
                    value={profile?.ideal_customer_profile || ''}
                    onChange={(e, data) => updateField('ideal_customer_profile', data.value)}
                    rows={6}
                    placeholder={IDEAL_CUSTOMER_PROFILE_PLACEHOLDER}
                />
            </div>

            <hr className={setupStyles.sectionDivider} aria-hidden="true" />

            <div className={setupStyles.step3Section}>
                <h3 className={setupStyles.categoryTitle}>Company Values</h3>
                <p className={setupStyles.step3Hint}>Add values that reflect how your organisation works. Each value can include a description and expected behaviour.</p>

                {companyValues.map((value, index) => {
                    if (editingValueIndex === index) return null;
                    return (
                        <div key={`${value.name}-${index}`} className={setupStyles.valueCard}>
                            <div className={setupStyles.orgSectionHeader}>
                                <p className={setupStyles.valueCardName}>{value.name}</p>
                                <div className={setupStyles.valueCardActions}>
                                    <button type="button" className={setupStyles.changeLink} onClick={() => handleEditSavedValue(index)}>
                                        Edit
                                    </button>
                                    <button type="button" className={setupStyles.valueDeleteBtn} onClick={() => handleDeleteSavedValue(index)} aria-label={`Delete ${value.name}`}>
                                        <Delete16Regular color="var(--Brand-Secondary, #c89741)" />
                                    </button>
                                </div>
                            </div>
                            {value.description ? <p className={setupStyles.valueCardDescription}>{value.description}</p> : null}
                            {value.expected_behavior ? <p className={setupStyles.valueCardBehavior}>{value.expected_behavior}</p> : null}
                        </div>
                    );
                })}

                <div className={setupStyles.valueForm}>
                    <div className={setupStyles.orgTextField}>
                        <p className={setupStyles.fieldLabel}>Company Value Name</p>
                        <Input
                            className={setupStyles.nameInput}
                            value={valueDraft.name}
                            onChange={(e, data) => updateValueDraftField('name', data.value)}
                            placeholder={COMPANY_VALUE_NAME_PLACEHOLDER}
                            appearance="outline"
                        />
                    </div>
                    <div className={setupStyles.orgTextField}>
                        <p className={setupStyles.fieldLabel}>Company Value Description</p>
                        <Textarea
                            className={setupStyles.orgTextarea}
                            value={valueDraft.description}
                            onChange={(e, data) => updateValueDraftField('description', data.value)}
                            rows={4}
                            placeholder={COMPANY_VALUE_DESCRIPTION_PLACEHOLDER}
                        />
                    </div>
                    <div className={setupStyles.orgTextField}>
                        <p className={setupStyles.fieldLabel}>Expected Behaviour</p>
                        <Textarea
                            className={setupStyles.orgTextarea}
                            value={valueDraft.expected_behavior}
                            onChange={(e, data) => updateValueDraftField('expected_behavior', data.value)}
                            rows={4}
                            placeholder={COMPANY_VALUE_BEHAVIOR_PLACEHOLDER}
                        />
                    </div>
                    <button type="button" className={setupStyles.saveAndAddAnotherBtn} disabled={!canSaveValue} onClick={handleSaveValueAndAddAnother}>
                        {editingValueIndex !== null ? 'Save value' : 'Save and add another'}
                    </button>
                </div>
            </div>

            <hr className={setupStyles.sectionDivider} aria-hidden="true" />

            <div className={setupStyles.step3Section}>
                <h3 className={setupStyles.categoryTitle}>Lens Pulse Preferences</h3>
                <div className={setupStyles.pulsePreferencesGrid}>{PULSE_PREFERENCE_KEYS.map(renderPulseField)}</div>
            </div>
        </>
    );
};

export default WorkspaceProfileSection;
